import { useEffect, useState } from "react";
import { Collapse } from "react-collapse";

import useEmailStore from "@store/useEmailStore";
import { useSuggestedTags } from "./hooks/useSuggestedTags";
import { useOptimisticTags } from "./hooks/useOptimisticTags";
import { useTimeoutRef } from "@hooks/useTimeoutRef";
import { cn } from "@utils/tailwind.util";
import { RelationObjectType } from "@enums/relations.enum";
import { CollapsibleSectionHeader } from "@components/Headers/CollapsibleSectionHeader";
import { TaggingComponentWithTags } from "../../TaggingComponent/TaggingComponentWithTags";
import { SuggestedTags } from "./SuggestedTags";

// Tag state is owned by the screen (single useOptimisticTags instance shared with NewEntityButtons).
type OptimisticTagsBundle = Pick<
  ReturnType<typeof useOptimisticTags>,
  "currentTags" | "updateTags" | "onRemoveTag" | "temporaryTags"
>;

interface Props extends OptimisticTagsBundle {
  contentType: RelationObjectType;
  className?: string;
  isUpdating?: boolean;
}

export function EmailTagsSection({
  contentType,
  className,
  isUpdating,
  currentTags,
  updateTags,
  onRemoveTag,
  temporaryTags,
}: Props) {
  const { threadId } = useEmailStore();
  const { data: suggestedOptions } = useSuggestedTags(contentType, threadId);

  const [isSectionOpen, setIsSectionOpen] = useState(false);
  const [isDropdownOpened, setIsDropdownOpened] = useState(false);
  const { schedule: scheduleSectionOpen } = useTimeoutRef();

  const handleToggleSection = () => setIsSectionOpen((prevState) => !prevState);

  const handleOpenDropdown = () => {
    if (!isSectionOpen) setIsSectionOpen(true);
    setIsDropdownOpened(true);
  };

  const handleCloseDropdown = () => setIsDropdownOpened(false);

  const tagsCount = currentTags?.length || 0;
  const excludedIds = currentTags.map((item) => item.object.id);

  useEffect(() => {
    if (!currentTags.length) return;
    // expand section once tags are available, with a short delay for smooth appearance
    scheduleSectionOpen(() => setIsSectionOpen(true), 100);
  }, [currentTags, scheduleSectionOpen]);

  return (
    <section className={cn("flex flex-col gap-3", className)}>
      <CollapsibleSectionHeader
        title="Tags"
        tagsCount={tagsCount}
        isOpen={isSectionOpen}
        onToggleSection={handleToggleSection}
        onOpenDropdown={handleOpenDropdown}
        isUpdating={isUpdating}
      />

      <Collapse isOpened={isSectionOpen}>
        <TaggingComponentWithTags
          contentType={contentType}
          keyForTypes={"EMAIL_THREADS"}
          onRemoveTag={onRemoveTag}
          onChangeTags={updateTags}
          selectedTags={currentTags}
          temporaryTags={temporaryTags}
          excludedIds={excludedIds}
          isDropdownOpened={isDropdownOpened}
          onCloseDropdown={handleCloseDropdown}
          dropdownClassNames="min-w-[280px]"
        />

        <SuggestedTags
          title={"You might also want to tag this thread to:"}
          suggestedTaggingOptions={suggestedOptions}
          onAddTags={updateTags}
        />
      </Collapse>
    </section>
  );
}
